import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";
import { useLocalizedLinks } from "@/hooks/useLocale";

const NAV_LINKS = [
  { key: "nav.home", to: "/" },
  { key: "nav.shopGuide", to: "/usfans-spreadsheet-guide" },
  { key: "nav.categories", to: "/#categories" },
  { key: "nav.guides", to: "/#guides" },
  { key: "nav.safety", to: "/#safety" },
  { key: "nav.faq", to: "/#faq" },
];

export default function Header() {
  const { t } = useTranslation();
  const { to: linkTo } = useLocalizedLinks();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to={linkTo("/")} className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500">
            <i className="ri-layout-grid-line text-lg text-background-50"></i>
          </span>
          <span className="font-heading text-lg font-bold text-foreground-950">{t("brand")}</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.key}
              to={linkTo(link.to)}
              className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
            >
              {t(link.key)}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("cta.exploreSpreadsheet")}
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <nav className="flex flex-col">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.key}
                to={linkTo(link.to)}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-primary-50 hover:text-primary-700"
              >
                {t(link.key)}
              </Link>
            ))}

            <p className="mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-600">
              {t("nav.categories")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700"
                >
                  <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                    <i className={`${cat.icon} text-base`}></i>
                  </span>
                  {t(`categories.items.${cat.id}`)}
                </a>
              ))}
            </div>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-center text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              {t("cta.exploreSpreadsheet")}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}